import React, { createContext, useContext, useState, useEffect } from 'react'; 
import { useAuth } from './AuthContext/AuthContext.jsx';
import { useFormContext } from './FormContext.jsx';
import { getSessionId } from './session';

const StepContext = createContext(); 

export const StepProvider = ({ children, initialStep }) => {
    const [currentStep, setCurrentStep] = useState(initialStep || 0);
    const [stepCompletionStatus, setStepCompletionStatus] = useState(new Array(17).fill(false)); 
    const [direction, setDirection] = useState(0);
    const [currentPageValue, setCurrentPageValue] = useState(0);
    const [currentSubStepProgress, setCurrentSubStepProgress] = useState(0);
    const [showAnimation, setShowAnimation] = useState(false);
    const [completedSteps, setCompletedSteps] = useState(new Set());
    const { user } = useAuth() || {};
    const { formData } = useFormContext() || {};
    const sessionId = getSessionId();

    // Map of main steps to their sub-step counts
    const subStepCounts = {
        0: 6,  // Parties & Property
        1: 3,  // Dates & Price
        2: 8,  // Provisions
        3: 1   // Review & Sign
    };

    // Update currentPageValue and subStepProgress when currentStep changes
    useEffect(() => {
        // Map currentStep to main page value
        let pageValue = 0;
        if (currentStep > 6 && currentStep < 10) {
            pageValue = 1; // Dates & Price
        } else if (currentStep > 9 && currentStep < 17) {
            pageValue = 2; // Provisions
        } else if (currentStep > 16) {
            pageValue = 3; // Review & Sign
        }
        setCurrentPageValue(pageValue);

        // Calculate sub-step progress
        const totalSubSteps = subStepCounts[pageValue];
        const currentSubStep = currentStep % totalSubSteps;
        setCurrentSubStepProgress(currentSubStep / totalSubSteps);
    }, [currentStep]);

    const markStepAsComplete = (step) => {
      setStepCompletionStatus(prevStatus => {
          const newStatus = [...prevStatus];
          newStatus[step] = true;
          return newStatus;
      });
    };

    const unsetStepAsComplete = (stepIndex) => {
      setStepCompletionStatus(prevStatus => {
          const newStatus = [...prevStatus];
          newStatus[stepIndex] = false;
          return newStatus;
      });
    };

    const handleNext = () => {
      setDirection(1);
      setCurrentStep(prev => {
        const nextStep = prev + 1;
        // New Relic event        
        if (window?.newrelic) {
          window.newrelic.recordCustomEvent('AnalyticEvent', { 
            element: 'NextButton', 
            action: 'nextClick', 
            step: nextStep,
            email: user?.email || 'unknown',
            sessionId: sessionId
          });
        }
        return nextStep;
      });
    };
  
    const handleBack = () => {
      setDirection(-1);
      unsetStepAsComplete(currentStep - 1);
      setCurrentStep(prev => {
        const prevStep = prev - 1;
        // New Relic event
        if (window?.newrelic) {
          window.newrelic.recordCustomEvent('AnalyticEvent', {
            element: 'BackButton',
            action: 'backClick',
            step: prevStep,
            email: user?.email || 'unknown',
            sessionId: sessionId
          });
        }
        return prevStep;
      });
    };

    const handleSearch = () => {
      setShowAnimation(true);
      // New Relic event
      if (window?.newrelic) {
        window.newrelic.recordCustomEvent('AnalyticEvent', {
          element: 'SearchButton',
          action: 'searchClick',
          email: user?.email || 'unknown',
          input: formData?.address ? JSON.stringify(formData.address) : '',
          sessionId: sessionId
        });
      }
    };

    const handleAnimationComplete = () => {
      setShowAnimation(false);
      setDirection(1);
      setCurrentStep(1);
    };

    return (
      <StepContext.Provider value={{ 
        currentStep, 
        stepCompletionStatus, 
        setCurrentStep, 
        markStepAsComplete, 
        unsetStepAsComplete, 
        handleNext, 
        handleBack, 
        handleSearch, 
        handleAnimationComplete,
        direction,
        currentPageValue,
        currentSubStepProgress,
        showAnimation,
        completedSteps,
        setCurrentPageValue,
        setCurrentSubStepProgress
      }}>
        {children}
      </StepContext.Provider>
    ); 
}; 

export const useStepContext = () => {
  const context = useContext(StepContext);
  if (!context) {
    throw new Error('useStepContext must be used within a StepProvider');
  }
  return context;
}; 